import { ArrayMaxSize, IsArray, IsDateString, IsIn, IsInt, IsOptional, IsString, MaxLength, Min } from 'class-validator';
import { LINKEDIN_TOUCH_KINDS, type LinkedInTouchKind } from '../entity/linkedin-touch.entity';

/** PD-17620–17623 — working a campaign: the queue, the list view and the person card. */

/** The queue's "Show" choices, in menu order. */
export const CAMPAIGN_QUEUE_FILTERS = ['due_now', 'calls', 'callbacks', 'linkedin', 'waiting_email', 'engaged', 'paused', 'ended', 'all'] as const;
export type CampaignQueueFilter = (typeof CAMPAIGN_QUEUE_FILTERS)[number];

export const CAMPAIGN_QUEUE_FILTER_LABELS: Readonly<Record<CampaignQueueFilter, string>> = {
	due_now: 'Due now',
	calls: 'Calls due',
	callbacks: 'Call-backs',
	linkedin: 'LinkedIn due',
	waiting_email: 'Waiting on email',
	engaged: 'Engaged',
	paused: 'Paused',
	ended: 'Ended',
	all: 'Everyone',
};

/** One person in a campaign, as the queue and the list view show them. */
export interface CampaignMemberRowDto {
	memberId: number;
	contactId: number | null;
	name: string | null;
	title: string | null;
	email: string | null;
	phone: string | null;
	linkedinUrl: string | null;
	clientId: number | null;
	accountName: string | null;
	city: string | null;
	state: string | null;
	/** active, paused or ended. */
	runState: string;
	endedReason: string | null;
	pausedUntil: string | null;
	pausedReason: string | null;
	stepNo: number | null;
	stepType: string | null;
	stepLabel: string | null;
	stepCount: number;
	/** The open step's status (pending, due, sending). */
	stepStatus: string | null;
	/** When the next action is due (a call-back's time when one is set). */
	dueAt: string | null;
	isCallback: boolean;
	/** The last thing that happened: "Call · No answer", "Email A sent". */
	lastTouch: string | null;
	lastTouchAt: string | null;
	/** The reply category's name, or "Replied". */
	reply: string | null;
	repUserId: number | null;
	repName: string | null;
}

export interface CampaignMembersPageDto {
	rows: Array<CampaignMemberRowDto>;
	total: number;
	/** Count per Show choice (same search, Mine and rep as the rows). */
	counts: Record<CampaignQueueFilter, number>;
}

/** The campaign's header line and the list's working columns. */
export interface CampaignWorkSummaryDto {
	campaignId: number;
	name: string;
	status: string;
	people: number;
	emailsSent: number;
	replies: number;
	calls: number;
	meetings: number;
	dueNow: number;
	/** PD-17734 — mailboxes of this campaign that cannot send; only on a single campaign's summary. */
	mailboxesDown?: Array<{ email: string; reason: string }>;
	mailboxesWorking?: number | null;
	/** People whose next email waits on a mailbox that is down. */
	peopleWaitingOnMailbox?: number;
}

export interface CampaignPersonCardTouchDto {
	kind: 'email' | 'call' | 'linkedin' | 'step' | 'reply' | 'note';
	title: string;
	detail: string | null;
	at: string | null;
	/** Call recording and transcript, when there is one. */
	callId: number | null;
	recordingUrl: string | null;
	transcript: string | null;
	summary: string | null;
	/** Who it was with, for the whole-account view. */
	personName: string | null;
	campaignName: string | null;
}

export interface CampaignPersonCardStepDto {
	stepNo: number;
	type: string;
	label: string;
	status: string | null;
	dueAt: string | null;
	doneAt: string | null;
	outcome: string | null;
	/** The copy that went out (subject or first line). */
	copy: string | null;
	/** What releases this step: "3 business days after Email A". */
	releasedBy: string | null;
}

export interface CampaignPersonAtAccountDto {
	contactId: number;
	name: string | null;
	title: string | null;
	email: string | null;
	lastContact: string | null;
	/** This person's run in this campaign, when they are in it. */
	memberId: number | null;
	inCampaign: boolean;
}

export interface CampaignAiNotesDto {
	whyThisCall: string | null;
	talkingPoints: Array<string>;
	personalLine: string | null;
	writtenAt: string | null;
	/** Why there are no notes (AI unavailable): the plain script still shows. */
	unavailable: string | null;
}

export interface CampaignPersonCardDto {
	/** PD-17649 — the campaign is linked to a show with an open booth time to book. */
	canBookBooth: boolean;
	member: CampaignMemberRowDto;
	campaign: { id: number; name: string; status: string };
	person: {
		contactId: number | null;
		name: string | null;
		title: string | null;
		email: string | null;
		phones: Array<{ label: string; number: string }>;
		linkedinUrl: string | null;
		language: string | null;
		isDoNotCall: boolean;
	};
	account: {
		clientId: number | null;
		name: string | null;
		address: string | null;
		city: string | null;
		state: string | null;
		stage: string | null;
		website: string | null;
		phone: string | null;
		competitor: string | null;
		facts: Array<{ label: string; value: string }>;
		opportunities: Array<{ id: number; name: string; stage: string | null }>;
		otherCampaigns: Array<{ id: number; name: string; runState: string }>;
	};
	/** IANA time zone of the account's address, for "10:24 AM PST". */
	timeZone: string | null;
	/** Merge facts for the scripts (caller falls back to the viewer). */
	facts: Record<string, string | null>;
	script: { body: string | null; isMine: boolean; myContentId: number | null; stepNo: number | null } | null;
	voicemail: string | null;
	gatekeeper: string | null;
	turnarounds: string | null;
	linkedinDraft: string | null;
	callbackAt: string | null;
	recent: Array<CampaignPersonCardTouchDto>;
	steps: Array<CampaignPersonCardStepDto>;
	people: Array<CampaignPersonAtAccountDto>;
	notes: CampaignAiNotesDto | null;
}

export class CampaignMemberIdsDto {
	@IsArray()
	@ArrayMaxSize(500)
	@IsInt({ each: true })
	public memberIds!: Array<number>;
}

export class AssignCampaignRepDto extends CampaignMemberIdsDto {
	@IsInt()
	@Min(1)
	public repUserId!: number;
}

export class BulkMarkCalledDto extends CampaignMemberIdsDto {
	@IsString()
	@MaxLength(100)
	public dispositionCode!: string;
}

/**
 * PD-17642 — one line of a campaign dashboard widget: something outstanding
 * and the page that resolves it. A line with nothing to do is not sent.
 */
export interface CampaignWidgetLineDto {
	key: string;
	/** The big number on My campaign work; null on lines that are a statement. */
	count: number | null;
	tone: 'danger' | 'warning' | 'success' | 'info' | 'default';
	title: string;
	detail: string | null;
	/** The link's words on Campaigns needing attention ("Approve", "Call list"). */
	action: string | null;
	path: string;
}

export interface CampaignWidgetDto {
	lines: Array<CampaignWidgetLineDto>;
}

/** PD-17626 — one of a person's campaign runs, as the person page lists it. */
export interface PersonCampaignRunDto {
	memberId: number;
	campaignId: number;
	campaignName: string;
	campaignStatus: string;
	runState: string;
	/** "Replied", "Meeting Booked" ... when the run ended. */
	endedLabel: string | null;
	replyCategory: string | null;
	accountName: string | null;
	startedAt: string | null;
	endedAt: string | null;
	/** "Step 3 of 5 · Call 2" while the run goes on. */
	currentStep: string | null;
	steps: Array<CampaignPersonCardStepDto>;
}

/** PD-17623 — a LinkedIn touch as the person page lists it. */
export interface LinkedInTouchRowDto {
	id: number;
	kind: LinkedInTouchKind;
	note: string | null;
	touchedAt: string;
	userName: string | null;
	campaignName: string | null;
}

export class LogLinkedInTouchDto {
	@IsIn(LINKEDIN_TOUCH_KINDS as unknown as Array<string>)
	public kind!: LinkedInTouchKind;

	/** Logged on a person anywhere (no campaign) when no member is given. */
	@IsOptional()
	@IsInt()
	public memberId?: number;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public note?: string;

	@IsOptional()
	@IsDateString()
	public at?: string;
}
